// src/components/Navbar.jsx
// ✅ Listens to "cartCountUpdated" event from Products.js / Cart.js for live badge
// ✅ Listens to "guestCartUpdated" for guest cart count in badge + drawer
// ✅ Guest: cart icon shown in desktop header + mobile header with badge
// ✅ Logged in: cart count badge in desktop right-actions + mobile footer
// ✅ No duplicate logic — single source of truth via events
// ✅ WhatsApp floating button → wa.me/9010236661
// ✅ LOGOUT: hard page reload via window.location so stale data from previous user is fully cleared
// ✅ FOOTER exported separately — place <Footer /> at the very bottom of your App/Layout

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

import HomeIcon from "../assets/home.png";
import CatIcon from "../assets/cat.png";
import CartIcon from "../assets/cart.png";
import OrderIcon from "../assets/order.png";
import AccountIcon from "../assets/account.png";
import WalletIcon from "../assets/wallet.png";
import sun from "../assets/sun.png";
import panPdf from "../assets/pdf.pdf";

// ─────────────────────────────────────────────────────────────
// GUEST CART COUNT from localStorage
// ─────────────────────────────────────────────────────────────
const readGuestCartCount = () => {
  try {
    const objRaw = localStorage.getItem("guest_cart");
    if (objRaw) {
      const obj = JSON.parse(objRaw);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        return Object.values(obj).reduce((sum, row) => sum + (Number(row?.qty) || 0), 0);
      }
    }
    const arrRaw = localStorage.getItem("guestCart");
    if (arrRaw) {
      const arr = JSON.parse(arrRaw);
      if (Array.isArray(arr)) return arr.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
    }
    return 0;
  } catch { return 0; }
};

const WA_NUMBER = "9182729720";
// We'll calculate the URL dynamically based on user state

// ─────────────────────────────────────────────────────────────
// SHARED CSS — injected once
// ─────────────────────────────────────────────────────────────
const SHARED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Inter:wght@400;500;600;700;800&display=swap');

:root {
  --hbg:   #0F1E3D;
  --header-bg: linear-gradient(135deg, #006600 100%, #006600 100%);
  --acc:   #C9A84C;
  --acc2:  #006600;
  --acc-t: rgba(0,102,0,.15);
  --acc-b: rgba(0,102,0,.40);
  --teal-t: rgba(0,102,0,.12);
  --teal-b: rgba(0,102,0,.30);
  --lite-green-t: rgba(144, 238, 144, 0.15);
  --lite-green: #32cd32;
  --wa:    #25D366;
  --safe:  env(safe-area-inset-bottom, 0px);
  --sh:    0 14px 36px rgba(0,0,0,.22);
}

/* ── Floating Actions Stack ── */
.wa-float { 
  position:fixed; bottom:20px; right:20px; z-index:9999; 
  display:flex; flex-direction:column; gap:12px; align-items:flex-end; 
}
@media(max-width:900px){ .wa-float{ display: none; } }

.wa-btn, .phone-btn, .cart-fab {
  width:56px; height:56px; border-radius:999px; border:none; cursor:pointer;
  display:grid; place-items:center; position:relative;
  transition:transform .2s cubic-bezier(.34,1.56,.64,1), box-shadow .2s;
}

.wa-btn { 
  background:var(--wa); 
  box-shadow:0 6px 24px rgba(37,211,102,.45),0 2px 8px rgba(0,0,0,.18); 
}
.wa-btn:hover { transform:scale(1.1); box-shadow:0 10px 32px rgba(37,211,102,.55),0 3px 12px rgba(0,0,0,.22); }

.phone-btn { 
  background:#3b82f6; 
  box-shadow:0 6px 24px rgba(59,130,246,.45),0 2px 8px rgba(0,0,0,.18); 
}
.phone-btn:hover { transform:scale(1.1); box-shadow:0 10px 32px rgba(59,130,246,.55),0 3px 12px rgba(0,0,0,.22); }
.phone-btn svg { width:26px; height:26px; stroke:#fff; display:block; }

.cart-fab { 
  background:var(--hbg); border:2px solid var(--acc);
  box-shadow:0 6px 24px rgba(15,30,61,.35),0 2px 8px rgba(0,0,0,.18); 
}
.cart-fab:hover { transform:scale(1.1); box-shadow:0 10px 32px rgba(15,30,61,.45),0 3px 12px rgba(0,0,0,.22); }
.cart-fab img { width:24px; height:24px; filter:brightness(0) invert(1); }

.wa-btn::before, .phone-btn::before {
  content:''; position:absolute; inset:-6px; border-radius:999px;
  border:2.5px solid rgba(255,255,255,.3); animation:waPulse 2.2s ease-out infinite;
}
@keyframes waPulse{
  0%{transform:scale(1);opacity:.7} 70%{transform:scale(1.35);opacity:0} 100%{transform:scale(1.35);opacity:0}
}

.wa-btn svg{ width:30px; height:30px; fill:#fff; display:block; }

.wa-tooltip {
  position:absolute; right:68px; top:50%; transform:translateY(-50%);
  background:#fff; color:#111; font-size:12px; font-weight:700;
  font-family:'Inter',sans-serif; white-space:nowrap; padding:6px 12px;
  border-radius:10px; box-shadow:0 6px 20px rgba(0,0,0,.15);
  pointer-events:none; border:1px solid rgba(0,0,0,.08); letter-spacing:.2px;
}
.wa-tooltip::after{
  content:''; position:absolute; top:50%; right:-6px; transform:translateY(-50%);
  border-width:6px 0 6px 7px; border-style:solid;
  border-color:transparent transparent transparent #fff;
}

.cart-badge-float {
  position:absolute; top:-5px; right:-5px; min-width:20px; height:20px; border-radius:999px;
  background:var(--acc2); color:#fff; font-size:10px; font-weight:900;
  display:grid; place-items:center; border:2px solid #fff;
}

/* ── Guest Desktop Header ── */
.nav-guest{ position:sticky; top:0; z-index:50; background:var(--header-bg); border-bottom:1px solid rgba(255,255,255,.07); }
.nav-guest-inner{ max-width:1400px; margin:0 auto; padding:0 32px; height:120px; display:flex; align-items:center; gap:0; }
.guest-logo-area{ display:flex; align-items:center; gap:10px; margin-right:48px; flex-shrink:0; cursor:pointer; }
.guest-logo-img{ height:110px; width:auto; object-fit:contain; }
.guest-nav-links{ display:flex; align-items:center; gap:4px; flex:1; }
.guest-nav-link{
  background:none; border:none; cursor:pointer;
  font-family:'Rajdhani','Inter',sans-serif; font-size:15px; font-weight:700;
  color:rgba(255,255,255,.82); letter-spacing:.8px; padding:8px 18px;
  position:relative; transition:color .18s; white-space:nowrap;
}
.guest-nav-link::after{
  content:''; position:absolute; bottom:0; left:50%; right:50%; height:2px;
  background:var(--acc); transition:left .22s,right .22s;
}     
.guest-nav-link:hover{ color:#fff; }
.guest-nav-link:hover::after,.guest-nav-link.active::after{ left:18px; right:18px; }
.guest-nav-link.active{ color:#fff; }
.guest-auth-btns{ display:flex; align-items:center; gap:8px; flex-shrink:0; margin-right:16px; }
.guest-login-btn{
  background:none; border:1px solid rgba(255,255,255,.30); border-radius:8px; cursor:pointer;
  font-family:'Rajdhani','Inter',sans-serif; font-size:14px; font-weight:700;
  color:rgba(255,255,255,.90); letter-spacing:.8px; padding:7px 18px; white-space:nowrap;
  transition:color .18s,background .18s,border-color .18s;
}
.guest-login-btn:hover{ color:#fff; background:rgba(255,255,255,.10); border-color:rgba(255,255,255,.55); }
.guest-register-btn{
  background:none; border:1px solid rgba(255,255,255,.30); border-radius:8px; cursor:pointer;
  font-family:'Rajdhani','Inter',sans-serif; font-size:14px; font-weight:700;
  color:rgba(255,255,255,.90); letter-spacing:.8px; padding:7px 18px; white-space:nowrap;
  transition:color .18s,background .18s,border-color .18s;
}
.guest-register-btn:hover{ color:#fff; background:rgba(255,255,255,.10); border-color:rgba(255,255,255,.55); }
.guest-socials{ display:flex; align-items:center; gap:10px; flex-shrink:0; }
.guest-social-btn{
  width:36px; height:36px; border-radius:50%;
  border:1.5px solid rgba(255,255,255,.30); background:transparent;
  display:grid; place-items:center; cursor:pointer; color:#fff;
  transition:border-color .18s,background .18s; flex-shrink:0;
}
.guest-social-btn:hover{ border-color:var(--acc); background:rgba(201,168,76,.12); }
.guest-social-btn svg{ width:16px; height:16px; fill:currentColor; }

/* ── Guest Dropdown ── */
.guest-nav-item { position: relative; display: inline-block; height: 100%; }
.guest-nav-dropdown {
  position: absolute; top: 100%; left: 18px; min-width: 200px;
  background: #fff; border-radius: 12px; box-shadow: 0 15px 40px rgba(0,0,0,0.2);
  opacity: 0; visibility: hidden; transform: translateY(10px);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  padding: 12px 0; z-index: 1000; border: 1px solid rgba(0,0,0,0.06);
}
.guest-nav-item:hover .guest-nav-dropdown { opacity: 1; visibility: visible; transform: translateY(0); }
.guest-dropdown-link {
  width: 100%; text-align: left; background: none; border: none; padding: 10px 20px;
  font-family: 'Rajdhani', 'Inter', sans-serif; font-size: 15px; font-weight: 700; color: #444;
  cursor: pointer; transition: background 0.15s, color 0.15s; display: block;
}
.guest-dropdown-link:hover { background: rgba(0, 102, 0, 0.05); color: #006600; }
.guest-nav-link .nav-arrow { font-size: 10px; margin-left: 6px; opacity: 0.7; }

/* ── Guest Mobile Header ── */
.nav-guest-mobile{ position:sticky; top:0; z-index:50; background:var(--header-bg); border-bottom:1px solid rgba(255,255,255,.07); display:none; }
.nav-guest-mobile-inner{ padding:10px 14px; display:flex; align-items:center; justify-content:space-between; position:relative; }
.guest-mobile-logo{ position:absolute; left:50%; transform:translateX(-50%); height:52px; width:52px; border-radius:10px; object-fit:contain; display:block; z-index:1; }
.guest-mobile-spacer{ flex:1; }
.guest-mobile-login-btn{
  height:34px; border:1px solid rgba(255,255,255,.30); border-radius:7px;
  background:transparent; color:#fff; font-size:12px; font-weight:700; cursor:pointer;
  padding:0 12px; flex-shrink:0; font-family:'Rajdhani','Inter',sans-serif; letter-spacing:.5px; transition:background .15s;
}
.guest-mobile-login-btn:hover{ background:rgba(255,255,255,.10); }
.guest-mobile-register-btn{
  height:34px; border:1px solid rgba(255,255,255,.30); border-radius:7px;
  background:transparent; color:#fff; font-size:12px; font-weight:700; cursor:pointer;
  padding:0 12px; flex-shrink:0; font-family:'Rajdhani','Inter',sans-serif; letter-spacing:.5px; transition:background .15s;
}
.guest-mobile-register-btn:hover{ background:rgba(255,255,255,.10); }
.guest-mobile-search-row{ padding:0 14px 10px; background:var(--header-bg); }

/* ── Mobile Login Icon Button ── */
.mobile-login-icon-wrap{ position:relative; flex-shrink:0; }
.mobile-login-icon-btn{
  width:38px; height:38px; border-radius:10px;
  border:1.5px solid rgba(255,255,255,.35); background:rgba(255,255,255,.10);
  cursor:pointer; display:grid; place-items:center; padding:0;
  transition:background .15s,border-color .15s; flex-shrink:0;
}
.mobile-login-icon-btn:hover{ background:rgba(255,255,255,.20); border-color:rgba(255,255,255,.60); }
.mobile-login-icon-btn svg{ width:22px; height:22px; fill:none; stroke:#fff; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; display:block; }
.login-dropdown{
  position:absolute; top:calc(100% + 8px); right:0; min-width:190px;
  background:#fff; border-radius:14px; box-shadow:0 16px 44px rgba(0,0,0,.22);
  border:1px solid rgba(0,0,0,.07); overflow:hidden;
  animation:loginDropIn .18s cubic-bezier(0.16,1,0.3,1);
  z-index:9999;
}
@keyframes loginDropIn{ from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
.login-dropdown-header{
  background:linear-gradient(135deg,#006600,#004d00); padding:12px 16px;
  font-size:11px; font-weight:800; color:rgba(255,255,255,.8); letter-spacing:1px;
  text-transform:uppercase;
}
.login-dropdown-item{
  width:100%; display:flex; align-items:center; gap:12px;
  padding:13px 16px; border:none; background:#fff; cursor:pointer;
  text-align:left; transition:background .13s;
}
.login-dropdown-item+.login-dropdown-item{ border-top:1px solid rgba(0,0,0,.06); }
.login-dropdown-item:hover{ background:rgba(0,102,0,.05); }
.login-dropdown-item-icon{
  width:34px; height:34px; border-radius:9px; display:grid; place-items:center;
  flex-shrink:0; font-size:16px;
}
.login-dropdown-item-icon.biz{ background:rgba(0,102,0,.10); border:1px solid rgba(0,102,0,.20); }
.login-dropdown-item-icon.inv{ background:rgba(201,168,76,.12); border:1px solid rgba(201,168,76,.30); }
.login-dropdown-item-label{ font-size:13px; font-weight:700; color:#222; font-family:'Rajdhani','Inter',sans-serif; letter-spacing:.3px; }
.login-dropdown-item-sub{ font-size:11px; color:#888; font-weight:500; margin-top:1px; }
.login-dropdown-item-arrow{ margin-left:auto; color:#999; font-size:14px; }

@media(max-width:900px){ .nav-guest{ display:none; } .nav-guest-mobile{ display:block; } }
@media(min-width:901px){ .nav-guest-mobile{ display:none; } }

/* ── Logged-In Desktop Header ── */
.nav-loggedin{ position:sticky; top:0; z-index:50; background:var(--header-bg); border-bottom:1px solid rgba(255,255,255,.07); }
.nav-loggedin-inner{
  max-width:1400px; margin:0 auto; padding:0 24px; height:100px;
  display:grid; grid-template-columns:auto 1fr auto; align-items:center; gap:16px;
}
.leftGroup{ display:flex; align-items:center; gap:10px; }
.menuBtn{
  width:40px; height:40px; border-radius:10px; border:1px solid rgba(255,255,255,.20);
  background:transparent; cursor:pointer; font-size:20px; color:#fff;
  display:grid; place-items:center; transition:background .15s; flex-shrink:0;
}
.menuBtn:hover{ background:rgba(255,255,255,.12); }
.headerLogo{ height:90px; width:90px; border-radius:10px; object-fit:contain; display:block; padding:2px; flex-shrink:0; }
.loggedin-nav-links{ display:flex; align-items:center; gap:2px; }
.loggedin-nav-link{
  background:none; border:none; cursor:pointer;
  font-family:'Rajdhani','Inter',sans-serif; font-size:14px; font-weight:700;
  color:#ffffff; letter-spacing:.5px; padding:6px 14px; border-radius:8px;
  position:relative; transition:color .15s,background .15s; white-space:nowrap;
}
.loggedin-nav-link:hover{ color:#fff; background:rgba(255,255,255,.08); }
.loggedin-nav-link.active{ color:var(--acc); background:rgba(201,168,76,.10); }

/* Search */
.searchWrap{ position:relative; width:100%; min-width:0; max-width:400px; }
.searchForm{
  display:flex; align-items:center; gap:6px; height:40px;
  border-radius:999px; border:1px solid rgba(255,255,255,.18);
  background:rgba(255,255,255,.06); padding:0 14px; width:100%;
  transition:background .18s,border-color .18s;
}
.searchForm:focus-within{ background:rgba(255,255,255,.96); border-color:var(--acc2); }
.searchIco{ font-size:13px; color:#ffffff; flex-shrink:0; }
.searchForm:focus-within .searchIco{ color:rgba(0,0,0,.45); }
.searchInput{ flex:1; border:none; outline:none; background:transparent; font-size:13px; font-weight:500; color:#fff; min-width:0; }
.searchForm:focus-within .searchInput{ color:#111; }
.searchInput::placeholder{ color:#ffffff; opacity:0.8; }
.searchForm:focus-within .searchInput::placeholder{ color:rgba(0,0,0,.35); }
.sClear{
  border:none; background:rgba(0,0,0,.10); border-radius:999px;
  height:22px; padding:0 8px; cursor:pointer; font-size:11px; font-weight:700; flex-shrink:0;
}
.sGo{
  border:none; background:var(--acc2); color:#fff; border-radius:999px;
  height:22px; padding:0 10px; cursor:pointer; font-size:12px; font-weight:800; flex-shrink:0;
}
.suggest{
  position:absolute; top:48px; left:0; right:0; background:#fff;
  border:1px solid rgba(0,0,0,.12); border-radius:14px; box-shadow:var(--sh); overflow:hidden; z-index:999;
}
.suggestTop{
  padding:9px 12px; font-size:12px; font-weight:700; color:rgba(0,0,0,.55);
  background:rgba(0,0,0,.025); border-bottom:1px solid rgba(0,0,0,.07);
}
.sItem{
  width:100%; display:flex; align-items:center; gap:10px;
  padding:10px 12px; border:none; background:#fff; text-align:left; cursor:pointer;
}
.sItem:hover{ background:var(--teal-t); }
.sThumb{ width:36px; height:36px; border-radius:9px; border:1px solid rgba(0,0,0,.08); object-fit:cover; flex-shrink:0; }
.sName{ font-size:13px; font-weight:700; color:#000; }
.sMeta{ font-size:12px; color:rgba(0,0,0,.55); margin-top:2px; }
.sEmpty{ padding:12px; font-size:13px; font-weight:600; color:rgba(0,0,0,.45); }
.sErr{ padding:9px 12px; font-size:12px; font-weight:700; color:#7f1d1d; background:rgba(239,68,68,.05); }

/* Right Actions */
.rightActions{ display:flex; align-items:center; gap:8px; flex-shrink:0; }
.iconBtn{
  width:40px; height:40px; border-radius:10px; border:1px solid rgba(255,255,255,.18);
  background:transparent; cursor:pointer; display:grid; place-items:center; padding:0;
  transition:background .15s; flex-shrink:0; position:relative;
}
.iconBtn:hover{ background:rgba(255,255,255,.12); }
.iconBtn img{ width:20px; height:20px; object-fit:contain; display:block; filter:brightness(0) invert(1); }
.cartBadge{
  position:absolute; top:-4px; right:-4px; min-width:16px; height:16px; border-radius:999px;
  background:var(--acc2); color:#fff; font-size:9px; font-weight:800;
  display:grid; place-items:center; padding:0 3px; line-height:1;
  border:1.5px solid var(--hbg); animation:popIn .2s ease;
}
@keyframes popIn{ from{transform:scale(.4);opacity:0} to{transform:scale(1);opacity:1} }
.avatarBtn{
  width:40px; height:40px; border-radius:10px; border:1.5px solid var(--acc);
  background:rgba(201,168,76,.12); cursor:pointer; display:grid; place-items:center;
  overflow:hidden; flex-shrink:0; font-weight:800; font-size:16px; color:var(--acc); transition:background .15s;
}
.avatarBtn:hover{ background:rgba(201,168,76,.22); }
.avatarBtn img{ width:100%; height:100%; object-fit:cover; display:block; }

/* Mobile Logged-In Header */
.mobile-loggedin-row1{
  display:flex; align-items:center; justify-content:space-between;
  padding:10px 14px 8px; background:var(--header-bg); position:relative;
}
.mobile-user-chip{
  display:flex; align-items:center; gap:8px; cursor:pointer; justify-content:flex-end; text-align:right;
  border:none; background:transparent; padding:0; height:44px; max-width: calc(50% - 30px);
  transition:opacity .18s;
}
.mobile-user-chip:hover{ opacity:0.8; }
.mobile-user-info{ min-width:0; flex:1; }
.mobile-user-name{
  font-size:13px; font-weight:700; color:#fff;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; line-height:1.2;
  font-family:'Rajdhani','Inter',sans-serif; letter-spacing:.4px;
}
.mobile-user-id{
  font-size:10px; font-weight:500; color:#ffffff; opacity:1;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; line-height:1.2; margin-top:1px;
}
.mobile-user-avatar{
  width:30px; height:30px; border-radius:999px; border:1.5px solid var(--acc);
  background:rgba(201,168,76,.22); display:grid; place-items:center;
  font-weight:800; font-size:12px; color:var(--acc); overflow:hidden; flex-shrink:0;
}
.mobile-user-avatar img{ width:100%; height:100%; object-fit:cover; display:block; }

/* Desktop User Info */
.desktop-user-area{
  display:flex; align-items:center; gap:12px; cursor:pointer;
  padding:4px 8px; border-radius:10px; transition:background .15s;
  margin-left:4px;
}
.desktop-user-area:hover{ background:rgba(255,255,255,.08); }
.desktop-user-info{ text-align:right; display:flex; flex-direction:column; justify-content:center; }
.desktop-user-name{
  font-size:14px; font-weight:700; color:#fff; line-height:1.2;
  font-family:'Rajdhani','Inter',sans-serif; letter-spacing:.3px;
}
.desktop-user-id{
  font-size:11px; font-weight:500; color:#ffffff; opacity:1;
  line-height:1.2; margin-top:2px;
}
.mobile-search-row{ display:none; padding:0 12px 10px; background:var(--header-bg); border-bottom:1px solid rgba(255,255,255,.07); }
@media(max-width:900px){
  .mobile-search-row{ display:block; }
  .loggedin-nav-links{ display:none; }
  .searchWrap{ max-width:100%; }
  .nav-loggedin-inner{ display:none; }
  .mobile-loggedin-header{ display:block; }
}
@media(min-width:901px){
  .nav-loggedin-inner{ display:grid; }
  .mobile-loggedin-header{ display:none; }
}

/* Drawer */
.overlay{ position:fixed; left:0 !important; top:0 !important; right:0 !important; bottom:0 !important; padding:0 !important; margin:0 !important; background:rgba(0,0,0,.45); z-index:9999; display:flex; justify-content:flex-start; outline:none; }
.drawer{
  width:min(300px,85%) !important; height:100vh !important; height:100dvh !important; background:#fff !important; 
  margin:0 !important; padding:0 !important;
  left:0 !important; top:0 !important; position:absolute !important; border-radius:0 !important;
  box-shadow:6px 0 40px rgba(0,0,0,.3); display:flex; flex-direction:column; animation:slideInLeft .22s ease;
  overflow:hidden;
}
@keyframes slideInLeft{ from{transform:translateX(-100%)} to{transform:translateX(0)} }
.drawerTopBar{
  background:var(--header-bg); padding:14px 16px; display:flex; align-items:center; justify-content:left;
  border-bottom:1px solid rgba(255,255,255,.08); flex-shrink:0; width:100% !important; box-sizing:border-box;
  min-height:70px; gap:12px;
}
.backBtn{
  width:36px; height:36px; border-radius:10px; border:1px solid rgba(255,255,255,.25);
  background:#fff; cursor:pointer; font-size:16px; color:#111; display:grid; place-items:center; flex-shrink:0;
  box-shadow:0 4px 12px rgba(0,0,0,0.15);
}
.drawerLogoWrap{ display:flex; align-items:center; gap:8px; min-width:0; }
.drawerLogoImg{ width:40px; height:40px; border-radius:8px; object-fit:contain; flex-shrink:0; border:1px solid rgba(255,255,255,.16); padding:2px; }
.drawerTitle{ font-weight:800; color:#fff; font-size:14px; letter-spacing:.3px; }
.drawerBody{ padding:0 0 100px; overflow-y:auto; flex:1; -webkit-overflow-scrolling:touch; box-sizing:border-box; }
.profileCard{
  background:linear-gradient(135deg,#004d00 0%,#006600 100%);
  border-radius:0 0 16px 16px; padding:20px 16px; margin-bottom:12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15); width:100% !important; box-sizing:border-box;
}
.pLeft{ display:flex; gap:10px; align-items:center; }
.pAvatar{
  width:44px; height:44px; border-radius:999px; background:rgba(201,168,76,.18); border:2px solid var(--acc);
  display:grid; place-items:center; font-weight:800; font-size:17px; color:var(--acc); overflow:hidden; flex-shrink:0;
}
.pAvatar img{ width:100%; height:100%; object-fit:cover; display:block; }
.pName{ font-weight:700; font-size:14px; color:#fff; line-height:1.2; word-break:break-word; text-transform:uppercase; letter-spacing:0.3px; }
.pLine{ margin-top:2px; font-size:11px; color:#ffffff; font-weight:600; opacity:1; }
.menuList{
  background:#fff; border-radius:12px; border:1px solid rgba(0,0,0,.08);
  box-shadow:0 4px 14px rgba(0,0,0,.05); overflow:hidden;
  margin: 0 10px;
}
.menuRow{
  width:100%; display:flex; align-items:center; gap:9px; padding:12px 10px;
  border:none; background:#fff; cursor:pointer; text-align:left; font-weight:600; color:#111; font-size:13.5px; transition:background .13s;
}
.menuRow+.menuRow{ border-top:1px solid rgba(0,0,0,.06); }
.menuRow:hover{ background:var(--teal-t); }
.menuRow.hasSub.subOpen{ background:var(--teal-t); }
.menuRow.websiteRow{ border-left:3px solid var(--acc2); }
.menuRow.websiteRow:hover{ background:rgba(30,204,176,.08); }
.mIcon{ width:30px; height:30px; border-radius:8px; background:var(--teal-t); border:1px solid var(--teal-b); display:grid; place-items:center; font-size:14px; flex-shrink:0; }
.mText{ flex:1; min-width:0; }
.mArrow{ font-size:15px; color:#006600; transition:transform .22s; display:inline-block; flex-shrink:0; }
.mArrow.rotated{ transform:rotate(90deg); }
.extBadge{ font-size:9px; font-weight:800; color:var(--acc2); background:var(--teal-t); border:1px solid var(--teal-b); border-radius:4px; padding:1px 4px; flex-shrink:0; letter-spacing:.3px; }
.subMenu{ overflow:hidden; max-height:0; transition:max-height .26s ease; background:rgba(0,102,0,0.02); border-top:1px solid var(--teal-b); }
.subMenu.open{ max-height:280px; }
.subRow{
  width:100%; display:flex; align-items:center; gap:8px; padding:10px 12px 10px 16px;
  border:none; background:transparent; cursor:pointer; text-align:left; font-size:13px; font-weight:600; color:#222; transition:background .13s;
}
.subRow+.subRow{ border-top:1px solid rgba(30,204,176,.10); }
.subRow:hover{ background:var(--teal-t); }
.subIcon{ width:24px; height:24px; border-radius:7px; background:var(--teal-t); border:1px solid var(--teal-b); display:grid; place-items:center; font-size:12px; flex-shrink:0; }
.subArrow{ margin-left:auto; font-size:13px; color:#006600; flex-shrink:0; }
.signOut{
  margin-top:8px; width:100%; padding:12px 10px; border-radius:12px;
  border:1px solid rgba(0,0,0,.08); background:#fff; box-shadow:0 3px 10px rgba(0,0,0,.05);
  font-weight:700; cursor:pointer; display:flex; align-items:center; gap:9px; text-align:left; font-size:13.5px; transition:background .13s;
}
.signOut:hover{ background:rgba(239,68,68,.06); }
.signOut .mIcon{ background:rgba(239,68,68,.10); border-color:rgba(239,68,68,.22); }
.wa-drawer-row{
  margin-top:8px; width:100%; padding:12px 10px; border-radius:12px;
  border:1px solid rgba(37,211,102,.25); background:rgba(37,211,102,.05);
  font-weight:700; cursor:pointer; display:flex; align-items:center; gap:9px; text-align:left; font-size:13.5px; transition:background .13s; color:#111;
}
.wa-drawer-row:hover{ background:rgba(37,211,102,.12); }
.wa-mIcon{ width:30px; height:30px; border-radius:8px; background:var(--wa); display:grid; place-items:center; flex-shrink:0; }
.wa-mIcon svg{ width:17px; height:17px; fill:#fff; }

/* Mobile Footer Nav */
.mFooterWrap{ position:fixed; left:0; right:0; bottom:0; z-index:400; padding-bottom:var(--safe); display:none; pointer-events:none; }
.mFooterBar{
  pointer-events:auto; width:100%; height:64px; background:var(--header-bg);
  border-top:1px solid rgba(255,255,255,.15); box-shadow:0 -6px 20px rgba(0,0,0,.25);
  display:grid; align-items:center; padding:5px 4px; gap:2px; border-radius:16px 16px 0 0;
}
.mFooterBar.loggedIn{ grid-template-columns:repeat(5,1fr); }
.mFBtn{
  border:none; background:transparent; cursor:pointer; height:52px; border-radius:10px;
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:3px;
  color:#ffffff; transition:background .15s,color .15s;
}
.mFBtn:hover{ background:rgba(255,255,255,.08); color:#fff; }
.mFBtn.active{ color:#fff; background:rgba(255,255,255,.12); }
.mFIco{ width:30px; height:30px; border-radius:9px; display:grid; place-items:center; position:relative; overflow:visible; background:transparent; }
.homeBtn .mFIco img{ width:32px; height:32px; border-radius:8px; object-fit:contain; filter:none !important; border:1.5px solid rgba(255,255,255,.25); }
.homeBtn.active .mFIco img{ border-color:#fff; box-shadow:0 0 0 2px rgba(255,255,255,.2); }
.mFIco img{ width:22px; height:22px; object-fit:contain; display:block; filter:brightness(0) invert(1); opacity:1; }
.mFBtn.active .mFIco img{ opacity:1; }
.mFIco svg { width: 20px; height: 20px; fill: currentColor; }
.mFLab{ font-size:10px; line-height:1; white-space:nowrap; font-weight:600; }
.mFBadge{
  position:absolute; top:-4px; right:-5px; min-width:16px; height:16px; border-radius:999px;
  background:var(--acc2); color:#fff; font-size:9px; font-weight:800;
  display:grid; place-items:center; padding:0 3px; line-height:1; border:1.5px solid #fff; z-index:1;
}
@media(max-width:900px){ .mFooterWrap.loggedIn{ display:block; } }
`;

// ─────────────────────────────────────────────────────────────
// WA SVG
// ─────────────────────────────────────────────────────────────
const WaSVG = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

const PhoneSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.88 12.88 0 0 0 .57 2.55 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.11-2.13a2 2 0 0 1 2.11-.45 12.88 12.88 0 0 0 2.55.57 2 2 0 0 1 1.72 2z" />
  </svg>
);

const ContactSVG = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
// NAVBAR COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [menuOpen, setMenuOpen] = useState(false);
  const [loginDropOpen, setLoginDropOpen] = useState(false);
  const loginDropRef = useRef(null);
  const drawerRef = useRef(null);
  const [openSub, setOpenSub] = useState(null);
  const [avatarOk, setAvatarOk] = useState(true);
  const [me, setMe] = useState(null);
  const role = String(me?.role || "").toUpperCase();

  const [cartCount, setCartCount] = useState(0);
  const [guestCartCount, setGuestCartCount] = useState(readGuestCartCount);
  const [waTooltip, setWaTooltip] = useState(false);
  const [phoneTooltip, setPhoneTooltip] = useState(false);

  const [q, setQ] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestErr, setSuggestErr] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);

  const API_ORIGIN = useMemo(() => {
    const base = (axiosInstance?.defaults?.baseURL || "").trim();
    if (!base) return window.location.origin;
    try { return new URL(base).origin; } catch { return window.location.origin; }
  }, []);

  const buildImgUrl = (path) => {
    if (!path) return "";
    const p = String(path).trim();
    if (!p) return "";
    if (p.startsWith("http://") || p.startsWith("https://")) return p;
    if (p.startsWith("/")) return `${API_ORIGIN}${p}`;
    return `${API_ORIGIN}/${p}`;
  };

  const avatarLetter = (me?.name || me?.email || "U")[0]?.toUpperCase();
  const profilePic = me?.profilePic ? buildImgUrl(me.profilePic) : "";
  const userIdText = me?.userID || me?.userId || me?.userid || me?.loginId || me?.id || "";
  const userTypeText = me?.userType || me?.type || me?.accountType || me?.role || "";
  const userName = me?.name || me?.email || "";

  useEffect(() => {
    const onCartCount = (e) => {
      const count = Number(e?.detail?.count ?? 0);
      if (token) setCartCount(count);
      else setGuestCartCount(count);
    };
    const onGuestCart = () => { setGuestCartCount(readGuestCartCount()); };
    const onCartUpdated = () => { if (!token) setGuestCartCount(readGuestCartCount()); };
    window.addEventListener("cartCountUpdated", onCartCount);
    window.addEventListener("guestCartUpdated", onGuestCart);
    window.addEventListener("cartUpdated", onCartUpdated);
    window.addEventListener("storage", onGuestCart);
    return () => {
      window.removeEventListener("cartCountUpdated", onCartCount);
      window.removeEventListener("guestCartUpdated", onGuestCart);
      window.removeEventListener("cartUpdated", onCartUpdated);
      window.removeEventListener("storage", onGuestCart);
    };
  }, [token]);

  // ─────────────────────────────────────────────────────────────
  // LOGOUT — hard reload so all React state + stale data is wiped
  // This prevents previous user's search suggestions, cart count,
  // profile data, etc. from being visible to the next user
  // ─────────────────────────────────────────────────────────────
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("guest_cart");
    localStorage.removeItem("guestCart");
    // Hard reload to Home — clears ALL React state and stale data
    window.location.href = "/";
  };

  const go = (path) => {
    setMenuOpen(false); setSuggestOpen(false); setOpenSub(null);
    const [p, hash] = path.split("#");
    if (hash && location.pathname.toLowerCase() === p.toLowerCase()) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    navigate(path);
  };

  const openExternal = (url) => {
    setMenuOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (token) axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    else delete axiosInstance.defaults.headers.common.Authorization;
  }, [token]);

  const loadMe = async () => {
    if (!token) { setMe(null); return; }
    try {
      const res = await axiosInstance.get("/api/users/me");
      setMe(res?.data?.user || null);
      setAvatarOk(true);
    } catch { setMe(null); }
  };

  const loadCartCount = async () => {
    if (!token) { setCartCount(0); setGuestCartCount(readGuestCartCount()); return; }
    try {
      const res = await axiosInstance.get("/api/cart");
      const d = res?.data || {};
      const items = d.CartItems || d.cartItems || d.items || [];
      let total = 0;
      if (Array.isArray(items)) {
        total = items.reduce((sum, it) => {
          const qty = Number(it?.qty ?? it?.quantity ?? it?.cartQty ?? 1);
          return sum + qty;
        }, 0);
      }
      if (total === 0 && d.itemsCount != null) total = Number(d.itemsCount) || 0;
      setCartCount(total);
    } catch { setCartCount(0); }
  };

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => { document.body.style.overflow = "auto"; };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false); setSuggestOpen(false); setOpenSub(null);
    // Clear search whenever route changes
    setQ(""); setSuggestions([]); setSuggestErr("");
  }, [location.pathname]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMe(); loadCartCount(); }, [token, location.pathname]);

  const getWaUrl = () => {
    const base = `https://wa.me/${WA_NUMBER}`;
    const msg = `Thank you for contacting SUN DISTRIBUTORS.\nName: \nCity: `;
    return `${base}?text=${encodeURIComponent(msg)}`;
  };

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  useEffect(() => {
    const onOutside = (e) => {
      const t = e.target;
      if (menuOpen && drawerRef.current && !drawerRef.current.contains(t)) setMenuOpen(false);
      if (suggestOpen && !t.closest(".searchWrap")) setSuggestOpen(false);
      if (loginDropOpen && loginDropRef.current && !loginDropRef.current.contains(t)) setLoginDropOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [menuOpen, suggestOpen, loginDropOpen]);

  const fetchSuggestions = async (text) => {
    const query = String(text || "").trim();
    if (!query) { setSuggestions([]); setSuggestErr(""); setSuggestLoading(false); return; }
    try {
      setSuggestLoading(true); setSuggestErr("");
      const res = await axiosInstance.get("/api/products", { params: { search: query } });
      const data = res?.data;
      const list = Array.isArray(data) ? data
        : Array.isArray(data?.products) ? data.products
          : Array.isArray(data?.rows) ? data.rows
            : Array.isArray(data?.data) ? data.data : [];
      setSuggestions((list || []).slice(0, 8));
    } catch (e) {
      setSuggestions([]);
      setSuggestErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Search failed");
    } finally { setSuggestLoading(false); }
  };

  const onSearchChange = (val) => {
    setQ(val);
    const query = String(val || "").trim();
    if (!query) { setSuggestOpen(false); setSuggestions([]); setSuggestErr(""); return; }
    setSuggestOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 300);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const query = String(q || "").trim();
    if (!query) return;
    setSuggestOpen(false);
    go(`/products?search=${encodeURIComponent(query)}`);
  };

  const pickThumb = (p) => {
    const img = p?.image || p?.thumbnail || p?.mainImage || (Array.isArray(p?.images) ? p.images[0] : null);
    return img ? buildImgUrl(img) : "";
  };

  const isActive = (path) => {
    const p = String(path || "").toLowerCase();
    const cur = String(location.pathname || "").toLowerCase();
    if (p === "/dashboard") return cur === "/dashboard" || cur === "/";
    if (p === "/") return cur === "/";
    return cur.startsWith(p);
  };

  const menuItems = [
    { key: "dashboard", label: "Dashboard", icon: "📊", to: "/Dashboard" },
    {
      key: "profile", label: "My Profile", icon: "👤",
      sub: [
        { label: "Profile", icon: "🧑", to: "/Myprofile" },
        { label: "Change Password", icon: "🔒", to: "/ChangePassword" },
        { label: "Welcome Letter", icon: "🧑", to: "/WelcomeLetter" },
        { label: "Account Details", icon: "📋", to: "/Referal" },
        { label: "My Registration", icon: "📝", to: "/MyRegistration" },
      ],
    },
    {
      key: "products", label: "Products", icon: "🛍️",
      sub: [
        { label: "All Products", icon: "📦", to: "/Products" },
        { label: "Categories", icon: "🗂️", to: "/AllCategories" },
        { label: "My Cart", icon: "🛒", to: "/cart" },
        { label: "Orders", icon: "📂", to: "/Order" },
      ],
    },
    { key: "wallet", label: "Wallet", icon: "💰", to: "/wallet" },
    { key: "orders", label: "My Orders", icon: "📦", to: "/Order" },
    { key: "withdraw", label: "Withdraw", icon: "💸", to: "/WalletWithdraw" },
    { key: "tree", label: "Tree View", icon: "🌳", to: "/Tree" },
    { key: "awards", label: "Awards & Achievements", icon: "🏆", to: "/AwardsAchivement" },
    { key: "daily", label: "Daily Wise", icon: "📊", to: "/DailyWise" },
    { key: "documents", label: "Documents", icon: "🗂️", to: "/documents" },
    { label: "SUN DISTRIBUTORS PLAN DOWNLOAD", key: "pan", icon: "📄", download: panPdf, fileName: "Sun_Entrepreneurs_Plan.pdf" },
  ];

  const guestNavLinks = [
    { label: "HOME", to: "/" },
    {
      label: "ABOUT",
      to: "/About",
      sub: [
        { label: "About Us", to: "/About" },
        { label: "SUN DISTRIBUTORS Life", to: "/sun-entrepreneurs-life" },
        { label: "Awards", to: "/awards" },
      ]
    },
    {
      label: "BUSSINESS",
      to: "/Bussiness",
      sub: [
        { label: "Why SUN DISTRIBUTORS", to: "/Bussiness#why-sun-entrepreneurs" },
        { label: "Status", to: "/Bussiness" },
        { label: "Opportunity", to: "/Bussiness#opportunity" },
        { label: "Register", to: "/Bussiness#register" },
        { label: "Benefits", to: "/Bussiness#benefits" },
        { label: "Idea", to: "/Bussiness" },
        { label: "Cancellation & Refund Policy", to: "/refund" },
        { label: "Terms & Conditions", to: "/terms" },
        { label: "Privacy Policy", to: "/privacy" },
      ]
    },
    { label: "PRODUCTS", to: "/Products" },
    { label: "DOCUMENTS", to: "/documents" },
    { label: "PLAN DOWNLOAD", download: panPdf, fileName: "Sun_Entrepreneurs_Plan.pdf" },
    { label: "CONTACT US", to: "/Contact" },
  ];

  const toggleSub = (key) => setOpenSub((prev) => (prev === key ? null : key));

  const SuggestDropdown = () => (
    suggestOpen ? (
      <div className="suggest">
        <div className="suggestTop">{suggestLoading ? "Searching..." : "Search Results"}</div>
        {suggestions.length === 0 && !suggestLoading ? <div className="sEmpty">No results</div> : null}
        {suggestions.map((p) => (
          <button key={p?.id} type="button" className="sItem"
            onClick={() => { setSuggestOpen(false); go(`/ProductDetails/${p.id}`); }}>
            <img className="sThumb"
              src={pickThumb(p) || "https://via.placeholder.com/80?text=P"}
              alt={p?.name || "p"}
              onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/80?text=P"; }} />
            <div style={{ minWidth: 0 }}>
              <div className="sName">{p?.name || p?.title || "Product"}</div>
              <div className="sMeta">{p?.price ? `₹${p.price}` : ""}</div>
            </div>
          </button>
        ))}
        {suggestErr ? <div className="sErr">{suggestErr}</div> : null}
      </div>
    ) : null
  );

  return (
    <>
      <style>{SHARED_CSS}</style>

      {/* Floating Actions Stack — Guest Only */}
      {!token && (
        <div className="wa-float">
          {/* Cart FAB */}
          <button type="button" className="cart-fab" onClick={() => go("/register")} aria-label="Cart">
            <img src={CartIcon} alt="Cart" />
            {guestCartCount > 0 && <span className="cart-badge-float">{guestCartCount > 99 ? "99+" : guestCartCount}</span>}
          </button>

          {/* WhatsApp Button */}
          <div style={{ position: 'relative' }}>
            {waTooltip && <div className="wa-tooltip">Need Support</div>}
            <button type="button" className="wa-btn" aria-label="WhatsApp Support"
              onClick={() => window.open(getWaUrl(), "_blank", "noopener,noreferrer")}
              onMouseEnter={() => setWaTooltip(true)}
              onMouseLeave={() => setWaTooltip(false)}>
              <WaSVG />
            </button>
          </div>

          {/* Phone Button */}
          <div style={{ position: 'relative' }}>
            {phoneTooltip && <div className="wa-tooltip">Need Support</div>}
            <button type="button" className="phone-btn" aria-label="Phone Support"
              onClick={() => window.open(`tel:${WA_NUMBER}`, "_self")}
              onMouseEnter={() => setPhoneTooltip(true)}
              onMouseLeave={() => setPhoneTooltip(false)}>
              <PhoneSVG />
            </button>
          </div>
        </div>
      )}

      {/* ── Guest Desktop Header ── */}
      {!token && (
        <header className="nav-guest">
          <div className="nav-guest-inner">
            <div className="guest-logo-area" onClick={() => go("/")}>
              <img src={sun} alt="SUN DISTRIBUTORS" className="guest-logo-img" />
            </div>
            <nav className="guest-nav-links">
              {guestNavLinks.map((link) => {
                const hasSub = Array.isArray(link.sub) && link.sub.length > 0;
                return (
                  <div key={link.label} className="guest-nav-item">
                    <button
                      className={`guest-nav-link${isActive(link.to) ? " active" : ""}`}
                      onClick={() => {
                        if (link.download) {
                          const a = document.createElement("a");
                          a.href = link.download;
                          a.download = link.fileName || "download.pdf";
                          a.click();
                        } else if (!hasSub) {
                          go(link.to);
                        }
                      }}
                    >
                      {link.label}
                      {hasSub && <span className="nav-arrow">▲</span>}
                    </button>
                    {hasSub && (
                      <div className="guest-nav-dropdown">
                        {link.sub.map((sub) => (
                          <button key={sub.label} className="guest-dropdown-link" onClick={() => go(sub.to)}>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
            <div className="guest-auth-btns">
              {/*} <button className={`guest-login-btn${isActive("/login") ? " active" : ""}`} onClick={() => go("/login")}>BUSINESS LOGIN</button>*/}
              <button className="guest-login-btn" style={{ borderColor: 'rgba(201,168,76,.5)', color: '#fff', background: 'rgba(201,168,76,.1)' }} onClick={() => window.location.href = "https://investment.mysun.in/investment-login"}>BUSINESS PARTNER LOGIN</button>
              {/*} <button className="guest-register-btn" onClick={() => go("/register")}>REGISTER</button>*/}
            </div>
            <div className="guest-socials">
              <button className="guest-social-btn" aria-label="Facebook" onClick={() => openExternal("https://facebook.com")}>
                <svg viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
              </button>
              <button className="guest-social-btn" aria-label="Instagram" onClick={() => openExternal("https://instagram.com")}>
                <svg viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" fill="none" stroke="currentColor" strokeWidth="2" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </button>
              <button className="guest-social-btn" aria-label="YouTube" onClick={() => openExternal("https://youtube.com")}>
                <svg viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#0F1E3D" /></svg>
              </button>
            </div>
          </div>
        </header>
      )}

      {/* ── Guest Mobile Header ── */}
      {!token && (
        <header className="nav-guest-mobile">
          <div className="nav-guest-mobile-inner">
            <button className="menuBtn" onClick={() => setMenuOpen(true)} aria-label="Open menu">☰</button>
            {/* Logo left-aligned */}
            <img src={sun} alt="Logo" className="guest-mobile-logo" style={{ position: 'static', transform: 'none', marginLeft: '8px', height: '44px', width: '44px', borderRadius: '10px', objectFit: 'contain' }} />
            {/* Brand name centered */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ textAlign: 'center', lineHeight: 1.1 }}>
                <div style={{ fontFamily: "'Rajdhani','Inter',sans-serif", fontSize: '15px', fontWeight: 800, color: '#fff', letterSpacing: '2px' }}>SUN</div>
                <div style={{ fontFamily: "'Rajdhani','Inter',sans-serif", fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,.80)', letterSpacing: '1px' }}>DISTRIBUTORS</div>
              </div>
            </div>
            {/* Mobile Login Icon with Dropdown */}
            <div className="mobile-login-icon-wrap" ref={loginDropRef} style={{ marginRight: '8px' }}>
              <button
                className="mobile-login-icon-btn"
                aria-label="Login options"
                onClick={() => setLoginDropOpen((v) => !v)}
              >
                <svg viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
              {loginDropOpen && (
                <div className="login-dropdown">
                  <div className="login-dropdown-header">Choose Login Type</div>
                  {/*} <button
                    className="login-dropdown-item"
                    onClick={() => { setLoginDropOpen(false); go("/login"); }}
                  >
                    <div>
                      <div className="login-dropdown-item-label">Business Login</div>
                    </div>
                    <span className="login-dropdown-item-arrow">›</span>
                  </button>*/}
                  <button
                    className="login-dropdown-item"
                    onClick={() => { setLoginDropOpen(false); window.location.href = "https://investment.mysun.in/investment-login"; }}
                  >
                    <div>
                      <div className="login-dropdown-item-label"> Login</div>
                    </div>
                    <span className="login-dropdown-item-arrow">›</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="guest-mobile-search-row">
            <div ref={searchWrapRef} className="searchWrap" style={{ maxWidth: "100%" }}>
              <form className="searchForm" onSubmit={submitSearch}>
                <span className="searchIco">🔍</span>
                <input ref={searchInputRef} className="searchInput" value={q}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search products..."
                  onFocus={() => { if (String(q).trim()) setSuggestOpen(true); }} />
                {q ? <button type="button" className="sClear" onClick={() => { setQ(""); setSuggestions([]); setSuggestErr(""); setSuggestOpen(false); }}>✕</button> : null}
              </form>
              <SuggestDropdown />
            </div>
          </div>
        </header>
      )}

      {/* ── Logged-In Header ── */}
      {token && (
        <header className="nav-loggedin">
          <div className="nav-loggedin-inner">
            <div className="leftGroup">
              <button className="menuBtn" onClick={() => setMenuOpen(true)} aria-label="Open menu">☰</button>
              <img src={sun} alt="Logo" className="headerLogo" />
              <nav className="loggedin-nav-links">
                <button className={`loggedin-nav-link${isActive("/dashboard") ? " active" : ""}`} onClick={() => go("/dashboard")}>Dashboard</button>
                <button className={`loggedin-nav-link${isActive("/Products") ? " active" : ""}`} onClick={() => go("/Products")}>Products</button>
                <button className={`loggedin-nav-link${isActive("/Order") ? " active" : ""}`} onClick={() => go("/Order")}>Orders</button>
                <button className={`loggedin-nav-link${isActive("/wallet") ? " active" : ""}`} onClick={() => go("/wallet")}>Wallet</button>
                <button className={`loggedin-nav-link${isActive("/Tree") ? " active" : ""}`} onClick={() => go("/Tree")}>Tree View</button>
                <button className={`loggedin-nav-link${isActive("/documents") ? " active" : ""}`} onClick={() => go("/documents")}>Documents</button>
                <button className="loggedin-nav-link" onClick={() => {
                  const a = document.createElement("a");
                  a.href = panPdf;
                  a.download = "Sun_Entrepreneurs_Plan.pdf";
                  a.click();
                }}>Plan Download</button>
              </nav>
            </div>
            <div ref={searchWrapRef} className="searchWrap">
              <form className="searchForm" onSubmit={submitSearch}>
                <span className="searchIco">🔍</span>
                <input ref={searchInputRef} className="searchInput" value={q}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search products..."
                  onFocus={() => { if (String(q).trim()) setSuggestOpen(true); }} />
                {q ? <button type="button" className="sClear" onClick={() => { setQ(""); setSuggestions([]); setSuggestErr(""); setSuggestOpen(false); }}>✕</button> : null}

              </form>
              <SuggestDropdown />
            </div>
            <div className="rightActions">
              <button type="button" className="iconBtn" aria-label={`Cart (${cartCount} items)`} onClick={() => go("/cart")}>
                <img src={CartIcon} alt="Cart" />
                {cartCount > 0 && <span className="cartBadge">{cartCount > 99 ? "99+" : cartCount}</span>}
              </button>
              <button type="button" className="iconBtn" aria-label="Wallet" onClick={() => go("/wallet")}>
                <img src={WalletIcon} alt="Wallet" />
              </button>

              <div className="desktop-user-area" onClick={() => go("/Myprofile")}>
                <div className="desktop-user-info">
                  <div className="desktop-user-name">{userName || "User"}</div>
                  {userIdText ? <div className="desktop-user-id">ID: {String(userIdText)}</div> : null}
                </div>
                <button type="button" className="avatarBtn" aria-label="Profile" style={{ margin: 0 }}>
                  {profilePic && avatarOk
                    ? <img src={profilePic} alt="avatar" onError={() => setAvatarOk(false)} />
                    : <span>{avatarLetter}</span>}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile logged-in header */}
          <div className="mobile-loggedin-header">
            <div className="mobile-loggedin-row1">
              <button className="menuBtn" onClick={() => setMenuOpen(true)} aria-label="Open menu">☰</button>
              <img src={sun} alt="Logo" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', height: 44, width: 44, borderRadius: 10, objectFit: "contain", zIndex: 1 }} />
              <button type="button" className="mobile-user-chip" onClick={() => go("/Myprofile")}>
                <div className="mobile-user-info">
                  <div className="mobile-user-name">{userName || "User"}</div>
                  {userIdText ? <div className="mobile-user-id">ID: {String(userIdText)}</div> : null}
                </div>
                <div className="mobile-user-avatar">
                  {profilePic && avatarOk
                    ? <img src={profilePic} alt="avatar" onError={() => setAvatarOk(false)} />
                    : <span>{avatarLetter}</span>}
                </div>
              </button>
            </div>
            <div className="mobile-search-row" style={{ display: "block" }}>
              <div className="searchWrap" style={{ maxWidth: "100%" }}>
                <form className="searchForm" onSubmit={submitSearch}>
                  <span className="searchIco">🔍</span>
                  <input className="searchInput" value={q}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Search products..."
                    onFocus={() => { if (String(q).trim()) setSuggestOpen(true); }} />
                  {q ? <button type="button" className="sClear" onClick={() => { setQ(""); setSuggestions([]); setSuggestErr(""); setSuggestOpen(false); }}>✕</button> : null}
                </form>
                <SuggestDropdown />
              </div>
            </div>
          </div>
        </header>
      )}

      {/* ── Drawer ── */}
      {menuOpen && (
        <div className="overlay" onClick={() => setMenuOpen(false)}>
          <div className="drawer" ref={drawerRef} onClick={(e) => e.stopPropagation()}>
            <div className="drawerTopBar">
              <button className="backBtn" onClick={() => setMenuOpen(false)}>✕</button>
              <div className="drawerLogoWrap">
                <img src={sun} alt="Logo" className="drawerLogoImg" />
                <div className="drawerTitle">MENU</div>
              </div>
            </div>
            <div className="drawerBody">
              {token ? (
                <>
                  <div className="profileCard">
                    <div className="pLeft">
                      <div className="pAvatar">
                        {profilePic && avatarOk
                          ? <img src={profilePic} alt="avatar" onError={() => setAvatarOk(false)} />
                          : <span>{avatarLetter}</span>}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="pName">{me?.name || "—"}</div>
                        {userIdText ? <div className="pLine">ID: {String(userIdText)}</div> : null}
                        {userTypeText ? <div className="pLine">Role: {String(userTypeText)}</div> : null}
                      </div>
                    </div>
                  </div>
                  <div className="menuList">
                    {menuItems.map((it) => {
                      const hasSub = Array.isArray(it.sub) && it.sub.length > 0;
                      const isOpen = openSub === it.key;
                      const isExt = !!it.external;
                      return (
                        <div key={it.key}>
                          <button
                            className={`menuRow${hasSub ? " hasSub" : ""}${hasSub && isOpen ? " subOpen" : ""}${isExt ? " websiteRow" : ""}`}
                            onClick={() => {
                              if (isExt) openExternal(it.external);
                              else if (it.download) {
                                const link = document.createElement("a");
                                link.href = it.download;
                                link.download = it.fileName || "download.pdf";
                                link.click();
                                setMenuOpen(false);
                              }
                              else if (hasSub) toggleSub(it.key);
                              else go(it.to);
                            }}>
                            <div className="mIcon">{it.icon}</div>
                            <div className="mText">{it.label}</div>
                            {it.key === "products" && cartCount > 0 && (
                              <span style={{ background: "var(--acc2)", color: "#fff", borderRadius: 999, fontSize: 10, fontWeight: 800, padding: "1px 6px", marginRight: 4 }}>{cartCount}</span>
                            )}
                            {isExt ? <span className="extBadge">↗</span> : <span className={`mArrow${hasSub && isOpen ? " rotated" : ""}`}>›</span>}
                          </button>
                          {hasSub && (
                            <div className={`subMenu${isOpen ? " open" : ""}`}>
                              {it.sub.map((sub) => (
                                <button key={sub.label} className="subRow" onClick={() => go(sub.to)}>
                                  <div className="subIcon">{sub.icon}</div>
                                  <span>{sub.label}</span>
                                  {sub.to === "/cart" && cartCount > 0 && (
                                    <span style={{ background: "var(--acc2)", color: "#fff", borderRadius: 999, fontSize: 10, fontWeight: 800, padding: "1px 6px", marginLeft: "auto", marginRight: 4 }}>{cartCount}</span>
                                  )}
                                  <span className="subArrow">›</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button className="wa-drawer-row" onClick={() => openExternal(getWaUrl())}>
                    <div className="wa-mIcon"><WaSVG /></div>
                    <div className="mText">WhatsApp Support</div>
                    <span className="mArrow" style={{ color: "#25D366" }}>›</span>
                  </button>
                  {role.includes("ADMIN") && (
                    <button className="signOut" onClick={() => go("/admin/dashboard")}>
                      <div className="mIcon">🛠</div>
                      <div className="mText">Admin Dashboard</div>
                      <span className="mArrow">›</span>
                    </button>
                  )}
                  <button className="signOut" onClick={logout}>
                    <div className="mIcon">⏻</div>
                    <div className="mText">Logout</div>
                    <span className="mArrow">›</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="menuList">
                    {[
                      { icon: "🏠", label: "Home", to: "/", key: "home" },
                      {
                        icon: "ℹ️", label: "About", to: "/About", key: "about",
                        sub: [
                          { label: "About Us", icon: "🏢", to: "/About" },
                          { label: "SUN DISTRIBUTORS Life", icon: "✨", to: "/sun-entrepreneurs-life" },
                          { label: "Awards", icon: "🏆", to: "/awards" },
                        ]
                      },
                      { icon: "🗂️", label: "Documents", to: "/documents", key: "docs_guest" },
                      { icon: "📄", label: "SUN DISTRIBUTORS PLAN DOWNLOAD", key: "pan_guest", download: panPdf, fileName: "Sun_Entrepreneurs_Plan.pdf" },
                      {
                        icon: "💼", label: "Business", to: "/Bussiness", key: "business",
                        sub: [
                          { label: "Why SUN DISTRIBUTORS", icon: "💎", to: "/Bussiness#why-sun-entrepreneurs" },
                          { label: "Opportunity", icon: "🚀", to: "/Bussiness#opportunity" },
                          { label: "Register", icon: "📝", to: "/Bussiness#register" },
                          { label: "Benefits", icon: "🎁", to: "/Bussiness#benefits" },
                          { label: "Idea", icon: "💡", to: "/Bussiness" },
                          { label: "Refund Policy", icon: "↩️", to: "/refund" },
                          { label: "Terms", icon: "📜", to: "/terms" },
                          { label: "Privacy", icon: "🔏", to: "/privacy" },
                        ]
                      },
                      { icon: "🛍️", label: "Products", to: "/Products", key: "prod" },
                      { icon: "📝", label: "Contact Us", to: "/Contact", key: "cont" },
                      { icon: "🛒", label: "Cart", to: "/register", key: "cart" },
                    ].map((item) => {
                      const hasSub = Array.isArray(item.sub) && item.sub.length > 0;
                      const isOpen = openSub === item.key;
                      return (
                        <div key={item.key || item.label}>
                          <button className={`menuRow${hasSub ? " hasSub" : ""}${hasSub && isOpen ? " subOpen" : ""}`}
                            onClick={() => {
                              if (item.download) {
                                const link = document.createElement("a");
                                link.href = item.download;
                                link.download = item.fileName || "download.pdf";
                                link.click();
                                setMenuOpen(false);
                              }
                              else if (hasSub) toggleSub(item.key);
                              else go(item.to);
                            }}>
                            <div className="mIcon">{item.icon}</div>
                            <div className="mText">{item.label}</div>
                            {item.to === "/cart" && guestCartCount > 0 && (
                              <span style={{ background: "#1ECCB0", color: "#fff", borderRadius: 999, fontSize: 10, fontWeight: 800, padding: "1px 7px", marginRight: 4 }}>{guestCartCount}</span>
                            )}
                            <span className={`mArrow${hasSub && isOpen ? " rotated" : ""}`}>›</span>
                          </button>
                          {hasSub && (
                            <div className={`subMenu${isOpen ? " open" : ""}`}>
                              {item.sub.map((sub) => (
                                <button key={sub.label} className="subRow" onClick={() => go(sub.to)}>
                                  <div className="subIcon">{sub.icon}</div>
                                  <span>{sub.label}</span>
                                  <span className="subArrow">›</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button className="wa-drawer-row" onClick={() => openExternal(getWaUrl())}>
                    <div className="wa-mIcon"><WaSVG /></div>
                    <div className="mText">WhatsApp Support</div>
                    <span className="mArrow" style={{ color: "#25D366" }}>›</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile Footer Nav (visible both before and after login) ── */}
      <div className="mFooterWrap loggedIn">
        <div className="mFooterBar loggedIn" style={{ gridTemplateColumns: token ? "repeat(6, 1fr)" : "repeat(6, 1fr)" }}>
          {token ? (
            <>
              <button type="button" className={`mFBtn homeBtn${isActive("/dashboard") ? " active" : ""}`} onClick={() => go("/dashboard")}>
                <div className="mFIco"><img src={sun} alt="home" /></div>
                <div className="mFLab">Home</div>
              </button>
              <button type="button" className={`mFBtn${isActive("/AllCategories") ? " active" : ""}`} onClick={() => go("/AllCategories")}>
                <div className="mFIco"><img src={CatIcon} alt="category" /></div>
                <div className="mFLab">Category</div>
              </button>
              {/* Create Short — Center Action Button */}
              <button
                type="button"
                className="mFBtn"
                onClick={() => go("/Products")}
                style={{ position: 'relative' }}
              >
                <div className="mFIco" style={{
                  width: '44px', height: '44px', borderRadius: '14px',
                  background: 'linear-gradient(135deg, #006600, #004d00)',
                  border: '2.5px solid rgba(255,255,255,0.30)',
                  boxShadow: '0 4px 16px rgba(0,102,0,0.5)',
                  overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '1px',
                }}>
                  <img src={sun} alt="SUN" style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '10px', filter: 'none' }} />
                </div>
                <div className="mFLab" style={{ fontSize: '9px', fontWeight: 700, color: '#fff' }}>Create Short</div>
              </button>
              <button type="button" className={`mFBtn${isActive("/Referal") ? " active" : ""}`} onClick={() => go("/Referal")}>
                <div className="mFIco"><img src={AccountIcon} alt="account" /></div>
                <div className="mFLab">Account</div>
              </button>
              <button type="button" className={`mFBtn${isActive("/cart") ? " active" : ""}`} onClick={() => go("/cart")}>
                <div className="mFIco">
                  <img src={CartIcon} alt="cart" />
                  {cartCount > 0 && <span className="mFBadge">{cartCount > 99 ? "99+" : cartCount}</span>}
                </div>
                <div className="mFLab">Cart</div>
              </button>
              <button type="button" className={`mFBtn${isActive("/Order") ? " active" : ""}`} onClick={() => go("/Order")}>
                <div className="mFIco"><img src={OrderIcon} alt="orders" /></div>
                <div className="mFLab">Orders</div>
              </button>
            </>
          ) : (
            <>
              <button type="button" className={`mFBtn homeBtn${isActive("/") ? " active" : ""}`} onClick={() => go("/")}>
                <div className="mFIco"><img src={sun} alt="home" /></div>
                <div className="mFLab">Home</div>
              </button>
              <button type="button" className="mFBtn" onClick={() => window.open(getWaUrl(), "_blank", "noopener,noreferrer")}>
                <div className="mFIco"><WaSVG /></div>
                <div className="mFLab">WhatsApp</div>
              </button>
              <button type="button" className={`mFBtn${isActive("/cart") ? " active" : ""}`} onClick={() => go("/register")}>
                <div className="mFIco">
                  <img src={CartIcon} alt="cart" />
                  {guestCartCount > 0 && <span className="mFBadge">{guestCartCount > 99 ? "99+" : guestCartCount}</span>}
                </div>
                <div className="mFLab">Cart</div>
              </button>
              <button type="button" className="mFBtn" onClick={() => window.open(`tel:${WA_NUMBER}`, "_self")}>
                <div className="mFIco"><PhoneSVG /></div>
                <div className="mFLab">Phone</div>
              </button>
              <button type="button" className={`mFBtn${isActive("/contact") ? " active" : ""}`} onClick={() => go("/contact")}>
                <div className="mFIco"><ContactSVG /></div>
                <div className="mFLab">Contact</div>
              </button>
              <button
                type="button"
                className="mFBtn"
                onClick={() => {
                  window.location.href =
                    "https://investment.mysun.in/investment-login";
                }}
              >
                <div className="mFIco">
                  <img src={AccountIcon} alt="account" />
                </div>

                <div className="mFLab">Login</div>
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FOOTER COMPONENT — export and place at the VERY BOTTOM of App/Layout
// Usage in App.jsx:
//   import Navbar, { Footer } from "./components/Navbar";
//   <Navbar />
//   <main>...</main>
//   <Footer />
// ═══════════════════════════════════════════════════════════════════
export function Footer() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";
  const currentYear = new Date().getFullYear();

  const go = (path) => navigate(path);
  const openExternal = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const getWaUrl = () => {
    const base = `https://wa.me/${WA_NUMBER}`;
    const msg = `Thank you for contacting SUN DISTRIBUTORS.\nName: \nCity: `;
    return `${base}?text=${encodeURIComponent(msg)}`;
  };

  const WaIcon = () => (
    <svg viewBox="0 0 24 24" style={{ width: "100%", height: "100%", fill: "currentColor" }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );

  return (
    <>
      <style>{`
        /* ══════════════════════════════════════════════════════
           FOOTER STYLES — fully visible, rich, bottom-of-page
        ══════════════════════════════════════════════════════ */
        .ft-root {
          font-family: 'Inter','Rajdhani',sans-serif;
          background: #0a1628;
          color: rgba(255,255,255,.65);
          position: relative;
          overflow: hidden;
        }
        .ft-root::before {
          content:'';
          position:absolute; inset:0; pointer-events:none;
          background:
            radial-gradient(ellipse 60% 40% at 10% 0%, rgba(201,168,76,.07) 0%, transparent 60%),
            radial-gradient(ellipse 50% 35% at 90% 100%, rgba(30,204,176,.06) 0%, transparent 60%);
        }
        .ft-rel { position:relative; z-index:1; }

        /* top shimmer bar */
        .ft-shimmer {
          height:4px;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(201,168,76,.4) 15%,
            #C9A84C 35%,
            #1ECCB0 65%,
            rgba(30,204,176,.4) 85%,
            transparent 100%
          );
        }

        /* ── STATS BAR ── */
        .ft-stats-bar {
          background: rgba(201,168,76,.06);
          border-bottom: 1px solid rgba(201,168,76,.13);
        }
        .ft-stats-wrap {
          max-width:1400px; margin:0 auto; padding:26px 40px;
          display:grid; grid-template-columns:repeat(4,1fr); gap:0;
        }
        .ft-stat {
          display:flex; align-items:center; gap:14px;
          padding:0 28px; border-right:1px solid rgba(255,255,255,.07);
        }
        .ft-stat:first-child { padding-left:0; }
        .ft-stat:last-child { border-right:none; }
        .ft-stat-ico { font-size:30px; flex-shrink:0; }
        .ft-stat-val {
          font-family:'Rajdhani','Inter',sans-serif;
          font-size:28px; font-weight:700; color:#C9A84C; line-height:1; letter-spacing:.5px;
        }
        .ft-stat-lbl { font-size:11.5px; color:rgba(255,255,255,.45); font-weight:500; margin-top:3px; }

        /* ── MAIN GRID ── */
        .ft-main {
          max-width:1400px; margin:0 auto;
          padding:60px 40px 48px;
          display:grid;
          grid-template-columns:1.9fr 1fr 1fr 1fr;
          gap:56px;
        }

        /* brand */
        .ft-brand-head { display:flex; align-items:center; gap:14px; margin-bottom:20px; }
        .ft-brand-img {
          width:66px; height:66px; border-radius:16px; object-fit:contain;
          border:2px solid rgba(201,168,76,.35); padding:5px;
          background:rgba(255,255,255,.04); flex-shrink:0;
          box-shadow:0 6px 24px rgba(201,168,76,.18);
        }
        .ft-brand-name {
          font-family:'Rajdhani','Inter',sans-serif;
          font-size:26px; font-weight:700; color:#fff; letter-spacing:1px; line-height:1.1;
        }
        .ft-brand-tag {
          font-size:10px; font-weight:700; color:#1ECCB0;
          letter-spacing:2.5px; text-transform:uppercase; margin-top:3px;
        }
        .ft-brand-bar {
          width:56px; height:3px;
          background:linear-gradient(90deg,#C9A84C,#1ECCB0);
          border-radius:3px; margin:16px 0 18px;
        }
        .ft-brand-desc {
          font-size:13.5px; line-height:1.78; color:rgba(255,255,255,.58);
          margin-bottom:24px; max-width:295px;
        }

        /* contact list */
        .ft-contact { list-style:none; margin:0 0 24px; padding:0; display:flex; flex-direction:column; gap:13px; }
        .ft-contact li { display:flex; align-items:flex-start; gap:12px; }
        .ft-ci {
          width:32px; height:32px; border-radius:9px; flex-shrink:0; margin-top:1px;
          background:rgba(201,168,76,.10); border:1px solid rgba(201,168,76,.25);
          display:grid; place-items:center; font-size:14px;
        }
        .ft-ct { font-size:13px; color:rgba(255,255,255,.62); line-height:1.55; font-weight:500; }
        .ft-ct a { color:rgba(255,255,255,.62); text-decoration:none; transition:color .15s; }
        .ft-ct a:hover { color:#C9A84C; }

        /* socials */
        .ft-soc-lbl { font-size:10px; font-weight:700; color:rgba(255,255,255,.38); letter-spacing:1.8px; text-transform:uppercase; margin-bottom:11px; }
        .ft-soc-row { display:flex; gap:9px; }
        .ft-soc-btn {
          width:40px; height:40px; border-radius:10px;
          border:1px solid rgba(255,255,255,.13); background:rgba(255,255,255,.04);
          display:grid; place-items:center; cursor:pointer;
          color:rgba(255,255,255,.60); transition:border-color .18s,background .18s,color .18s,transform .15s; flex-shrink:0;
        }
        .ft-soc-btn:hover { border-color:#C9A84C; background:rgba(201,168,76,.12); color:#fff; transform:translateY(-3px); }
        .ft-soc-btn.wa:hover { border-color:#25D366; background:rgba(37,211,102,.12); color:#25D366; }
        .ft-soc-btn svg { width:16px; height:16px; fill:currentColor; }

        /* column */
        .ft-col-head {
          position:relative; margin-bottom:22px; padding-bottom:13px;
        }
        .ft-col-head::after {
          content:''; position:absolute; bottom:0; left:0; right:0; height:1px;
          background:linear-gradient(90deg,rgba(201,168,76,.40),transparent);
        }
        .ft-col-title {
          font-family:'Rajdhani','Inter',sans-serif;
          font-size:16px; font-weight:700; color:#fff; letter-spacing:1.4px; text-transform:uppercase;
          display:flex; align-items:center; gap:8px;
        }
        .ft-col-dot { width:7px; height:7px; border-radius:50%; background:#1ECCB0; flex-shrink:0; }

        /* link list */
        .ft-links { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:2px; }
        .ft-links li button {
          background:none; border:none; cursor:pointer; padding:8px 9px;
          font-size:13.5px; font-weight:500; color:rgba(255,255,255,.60);
          text-align:left; font-family:'Inter',sans-serif;
          display:flex; align-items:center; gap:9px; width:100%;
          border-radius:8px; transition:background .13s,color .13s,padding-left .15s;
        }
        .ft-links li button:hover { background:rgba(255,255,255,.06); color:#fff; padding-left:16px; }
        .ft-link-arr { font-size:13px; color:#1ECCB0; font-weight:800; margin-left:auto; opacity:0; transition:opacity .13s; flex-shrink:0; }
        .ft-links li button:hover .ft-link-arr { opacity:1; }
        .ft-link-icon { font-size:14px; flex-shrink:0; }

        /* newsletter */
        .ft-nl {
          margin-top:24px; background:rgba(255,255,255,.03);
          border:1px solid rgba(255,255,255,.09); border-radius:13px; padding:18px;
        }
        .ft-nl-title { font-size:13px; font-weight:700; color:rgba(255,255,255,.78); margin-bottom:4px; }
        .ft-nl-sub { font-size:11.5px; color:rgba(255,255,255,.40); margin-bottom:12px; line-height:1.5; }
        .ft-nl-form { display:flex; border-radius:10px; overflow:hidden; border:1px solid rgba(255,255,255,.14); background:rgba(255,255,255,.04); }
        .ft-nl-input {
          flex:1; border:none; outline:none; background:transparent;
          padding:0 13px; font-size:13px; color:#fff; min-width:0;
          font-family:'Inter',sans-serif; height:42px;
        }
        .ft-nl-input::placeholder { color:rgba(255,255,255,.28); }
        .ft-nl-btn {
          border:none; background:linear-gradient(135deg,#C9A84C 0%,#b8922e 100%);
          color:#fff; padding:0 18px; cursor:pointer;
          font-size:12px; font-weight:700; font-family:'Rajdhani',sans-serif;
          letter-spacing:.8px; text-transform:uppercase; transition:opacity .15s;
          height:42px; white-space:nowrap; flex-shrink:0;
        }
        .ft-nl-btn:hover { opacity:.88; }

        /* WA CTA banner */
        .ft-wa-wrap { max-width:1400px; margin:0 auto; padding:0 40px 44px; }
        .ft-wa-inner {
          background:linear-gradient(135deg,rgba(37,211,102,.10) 0%,rgba(18,140,126,.07) 100%);
          border:1px solid rgba(37,211,102,.20); border-radius:16px;
          padding:22px 30px; display:flex; align-items:center; gap:22px; flex-wrap:wrap;
        }
        .ft-wa-ico {
          width:52px; height:52px; border-radius:14px; background:#25D366;
          display:grid; place-items:center; flex-shrink:0;
          box-shadow:0 4px 20px rgba(37,211,102,.38);
        }
        .ft-wa-ico svg { width:28px; height:28px; fill:#fff; }
        .ft-wa-txt { flex:1; min-width:140px; }
        .ft-wa-txt-h { font-size:16px; font-weight:700; color:#fff; margin-bottom:3px; }
        .ft-wa-txt-s { font-size:13px; color:rgba(255,255,255,.55); font-weight:500; }
        .ft-wa-cta {
          border:none; background:#25D366; color:#fff; border-radius:10px;
          padding:11px 24px; cursor:pointer; font-size:13.5px; font-weight:700;
          font-family:'Rajdhani',sans-serif; letter-spacing:.8px; transition:background .15s,transform .15s;
          white-space:nowrap; flex-shrink:0;
        }
        .ft-wa-cta:hover { background:#1da84e; transform:scale(1.03); }
        .ft-wa-num { font-size:13px; color:rgba(255,255,255,.45); font-weight:600; white-space:nowrap; }

        /* ── LOGGED-IN FOOTER ── */
        .ft-li-main {
          max-width:1400px; margin:0 auto;
          padding:52px 40px 44px;
          display:grid; grid-template-columns:1.9fr 1fr 1fr; gap:56px;
        }
        .ft-li-head { display:flex; align-items:center; gap:12px; margin-bottom:16px; }
        .ft-li-logo {
          width:56px; height:56px; border-radius:13px; object-fit:contain;
          border:2px solid rgba(201,168,76,.30); padding:4px;
          background:rgba(255,255,255,.04); flex-shrink:0;
          box-shadow:0 4px 16px rgba(201,168,76,.14);
        }
        .ft-li-name {
          font-family:'Rajdhani','Inter',sans-serif;
          font-size:22px; font-weight:700; color:#fff; letter-spacing:.8px; line-height:1.1;
        }
        .ft-li-sub { font-size:10px; font-weight:700; color:#1ECCB0; letter-spacing:2px; text-transform:uppercase; margin-top:2px; }
        .ft-li-bar { width:44px; height:3px; background:linear-gradient(90deg,#C9A84C,#1ECCB0); border-radius:3px; margin:14px 0 16px; }
        .ft-li-desc { font-size:13px; line-height:1.72; color:rgba(255,255,255,.55); margin-bottom:20px; max-width:310px; }
        .ft-li-grid { display:grid; grid-template-columns:1fr 1fr; gap:9px; }
        .ft-li-btn {
          display:flex; align-items:center; gap:8px;
          border:1px solid rgba(255,255,255,.10); border-radius:10px;
          background:rgba(255,255,255,.04); color:rgba(255,255,255,.65);
          font-size:12.5px; font-weight:600; cursor:pointer; padding:10px 13px;
          font-family:'Inter',sans-serif; transition:background .15s,border-color .15s,color .15s,transform .13s;
          overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        }
        .ft-li-btn:hover { background:rgba(201,168,76,.12); border-color:rgba(201,168,76,.30); color:#fff; transform:translateY(-2px); }

        /* ── BOTTOM BAR (shared) ── */
        .ft-bot { background:rgba(0,0,0,.30); border-top:1px solid rgba(255,255,255,.07); }
        .ft-bot-inner {
          max-width:1400px; margin:0 auto; padding:20px 40px;
          display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap;
        }
        .ft-copy { font-size:13px; color:rgba(255,255,255,.38); font-weight:500; line-height:1.6; }
        .ft-copy span { color:#C9A84C; font-weight:700; }
        .ft-bot-links { display:flex; align-items:center; gap:4px; flex-wrap:wrap; }
        .ft-bot-link {
          font-size:12.5px; color:rgba(255,255,255,.38); background:none; border:none; cursor:pointer;
          font-family:'Inter',sans-serif; font-weight:500; padding:5px 11px; border-radius:7px; transition:background .13s,color .13s;
        }
        .ft-bot-link:hover { background:rgba(255,255,255,.07); color:rgba(255,255,255,.80); }
        .ft-bot-sep { color:rgba(255,255,255,.15); font-size:12px; user-select:none; }
        .ft-badge {
          display:inline-flex; align-items:center; gap:6px;
          background:rgba(201,168,76,.10); border:1px solid rgba(201,168,76,.22);
          border-radius:20px; padding:5px 12px;
          font-size:11.5px; font-weight:700; color:#C9A84C; letter-spacing:.3px;
        }
        .ft-badge-dot {
          width:7px; height:7px; border-radius:50%; background:#1ECCB0; flex-shrink:0;
          animation:ftblink 2s ease-in-out infinite;
        }
        @keyframes ftblink{ 0%,100%{opacity:1} 50%{opacity:.25} }

        /* WA row in logged-in bottom */
        .ft-wa-row {
          display:flex; align-items:center; gap:8px;
          background:rgba(37,211,102,.07); border:1px solid rgba(37,211,102,.18);
          border-radius:9px; padding:8px 14px; cursor:pointer;
          color:#25D366; font-size:13px; font-weight:700; font-family:'Inter',sans-serif;
          transition:background .15s; flex-shrink:0;
        }
        .ft-wa-row:hover { background:rgba(37,211,102,.18); }
        .ft-wa-row-ico { width:18px; height:18px; flex-shrink:0; }

        /* ── RESPONSIVE ── */
        @media(max-width:1100px){
          .ft-main{ grid-template-columns:1.5fr 1fr 1fr; }
          .ft-main>*:last-child{ display:none; }
          .ft-stats-wrap{ grid-template-columns:repeat(2,1fr); gap:20px; }
          .ft-stat{ border-right:none; padding:0; }
          .ft-li-main{ grid-template-columns:1.5fr 1fr; }
        }
        @media(max-width:760px){
          .ft-main{ grid-template-columns:1fr; padding:36px 20px 28px; gap:32px; }
          .ft-stats-wrap{ grid-template-columns:repeat(2,1fr); padding:20px 20px; gap:16px; }
          .ft-li-main{ grid-template-columns:1fr; padding:32px 20px 28px; gap:28px; }
          .ft-wa-wrap{ padding:0 20px 36px; }
          .ft-bot-inner{ flex-direction:column; align-items:flex-start; gap:12px; padding:16px 20px; }
          .ft-bot-links{ gap:2px; }
          .ft-li-grid{ grid-template-columns:1fr 1fr; }
        }
        /* on mobile, logged-in footer leaves room for fixed bottom nav */
        @media(max-width:900px){ .ft-loggedin-spacer{ height:70px; } }
      `}</style>

      <footer className="ft-root">
        <div className="ft-rel">
          <div className="ft-shimmer" />

          {/* ════════════════════════ GUEST FOOTER ════════════════════════ */}
          {!token && (
            <>
              {/* Stats */}
              <div className="ft-stats-bar">
                <div className="ft-stats-wrap">
                  {[
                    { ico: "👥", val: "50,000+", lbl: "Active Members" },
                    { ico: "🛍️", val: "1,200+", lbl: "Products Available" },
                    { ico: "🌍", val: "28+", lbl: "States Covered" },
                    { ico: "⭐", val: "4.8 / 5", lbl: "Customer Rating" },
                  ].map(s => (
                    <div className="ft-stat" key={s.lbl}>
                      <div className="ft-stat-ico">{s.ico}</div>
                      <div>
                        <div className="ft-stat-val">{s.val}</div>
                        <div className="ft-stat-lbl">{s.lbl}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Main columns */}
              <div className="ft-main">
                {/* Brand */}
                <div>
                  <div className="ft-brand-head">
                    <img src={sun} alt="SUN DISTRIBUTORS" className="ft-brand-img" />
                    <div>
                      <div className="ft-brand-name">SUN DISTRIBUTORS</div>
                      <div className="ft-brand-tag">Empowering Businesses</div>
                    </div>
                  </div>
                  <div className="ft-brand-bar" />
                  <p className="ft-brand-desc">
                    SUN DISTRIBUTORS is India's trusted direct-selling network, empowering individuals with quality products, real business opportunities and a proven MLM ecosystem.
                  </p>
                  <ul className="ft-contact">
                    {[
                      { ico: "📍", txt: <a href="https://www.google.com/maps/search/?api=1&query=F.+NO+101,+48-13-17,+Sringar,+Janakirama+street,+Near+RTC+Complex,+Visakhapatnam-16" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>SUN Distributors Pvt. Ltd.<br /> F. NO 101, 48-13-17, Sringar, Janakirama street, Near RTC Complex, Visakhapatnam-16</a> },
                      { ico: "📞", txt: <a href={`tel:+91${WA_NUMBER}`}>+91 {WA_NUMBER}</a> },
                      { ico: "✉️", txt: <a href="mailto:info@mysun.in">info@mysun.in</a> },
                      { ico: "🌐", txt: <a href="https://mysun.in" target="_blank" rel="noopener noreferrer">www.mysun.in</a> },
                    ].map((c, i) => (
                      <li key={i}>
                        <div className="ft-ci">{c.ico}</div>
                        <div className="ft-ct">{c.txt}</div>
                      </li>
                    ))}
                  </ul>
                  <div className="ft-soc-lbl">Follow Us</div>
                  <div className="ft-soc-row">
                    <button className="ft-soc-btn" aria-label="Facebook" onClick={() => openExternal("https://facebook.com")}>
                      <svg viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
                    </button>
                    <button className="ft-soc-btn" aria-label="Instagram" onClick={() => openExternal("https://instagram.com")}>
                      <svg viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" fill="none" stroke="currentColor" strokeWidth="2" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                    <button className="ft-soc-btn" aria-label="YouTube" onClick={() => openExternal("https://youtube.com")}>
                      <svg viewBox="0 0 24 24"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.96-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" /><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#0a1628" /></svg>
                    </button>
                    <button className="ft-soc-btn wa" aria-label="WhatsApp" onClick={() => openExternal(getWaUrl())}>
                      <WaIcon />
                    </button>
                  </div>
                </div>

                {/* Quick Links */}
                <div>
                  <div className="ft-col-head">
                    <div className="ft-col-title"><span className="ft-col-dot" />Quick Links</div>
                  </div>
                  <ul className="ft-links">
                    {[
                      { ico: "🏠", lbl: "Home", to: "/" },
                      { ico: "🏢", lbl: "About Us", to: "/About" },
                      { ico: "✨", lbl: "SUN DISTRIBUTORS Life", to: "/sun-entrepreneurs-life" },
                      { ico: "🏆", lbl: "Awards", to: "/awards" },
                      { ico: "💼", lbl: "Business Plan", to: "/Bussiness" },
                      { ico: "🛍️", lbl: "All Products", to: "/Products" },
                      { ico: "📞", lbl: "Contact Us", to: "/Contact" },
                      { ico: "🔑", lbl: "Login", action: "loginDrop" },
                      { ico: "📝", lbl: "Register", to: "/register" },
                    ].map(l => (
                      <li key={l.lbl}>
                        <button onClick={() => {
                          if (l.action === 'loginDrop') {
                            const choice = window.confirm('Click OK for Business Login\nClick Cancel for Business Partner Login');
                            if (choice) go('/login');
                            else window.location.href = 'https://investment.mysun.in/investment-login';
                          } else {
                            go(l.to);
                          }
                        }}>
                          <span className="ft-link-icon">{l.ico}</span>{l.lbl}
                          <span className="ft-link-arr">›</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Products */}
                <div>
                  <div className="ft-col-head">
                    <div className="ft-col-title"><span className="ft-col-dot" />Our Products</div>
                  </div>
                  <ul className="ft-links">
                    {[
                      { ico: "🗂️", lbl: "All Categories", to: "/AllCategories" },
                      { ico: "🛒", lbl: "Shop Now", to: "/Products" },
                      { ico: "🛍️", lbl: "My Cart", to: "/register" },
                      { ico: "🆕", lbl: "New Arrivals", to: "/Products" },
                      { ico: "🔥", lbl: "Best Sellers", to: "/Products" },
                      { ico: "🎁", lbl: "Offers & Deals", to: "/Products" },
                    ].map(l => (
                      <li key={l.lbl}>
                        <button onClick={() => go(l.to)}>
                          <span className="ft-link-icon">{l.ico}</span>{l.lbl}
                          <span className="ft-link-arr">›</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="ft-nl">
                    <div className="ft-nl-title">📬 Stay Updated</div>
                    <div className="ft-nl-sub">Get the latest offers & news in your inbox.</div>
                    <div className="ft-nl-form">
                      <input className="ft-nl-input" type="email" placeholder="Your email address" />
                      <button className="ft-nl-btn" type="button">Subscribe</button>
                    </div>
                  </div>
                </div>

                {/* Support */}
                <div>
                  <div className="ft-col-head">
                    <div className="ft-col-title"><span className="ft-col-dot" />Support</div>
                  </div>
                  <ul className="ft-links">
                    {[
                      { ico: "🆘", lbl: "Help Centre", to: "/Contact" },
                      { ico: "🔏", lbl: "Privacy Policy", to: "/privacy" },
                      { ico: "📜", lbl: "Terms & Conditions", to: "/terms" },
                      { ico: "↩️", lbl: "Refund Policy", to: "/refund" },
                      { ico: "🚚", lbl: "Shipping Policy", to: "/shipping" },
                      { ico: "❓", lbl: "FAQ", to: "/faq" },
                    ].map(l => (
                      <li key={l.lbl}>
                        <button onClick={() => go(l.to)}>
                          <span className="ft-link-icon">{l.ico}</span>{l.lbl}
                          <span className="ft-link-arr">›</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* WA CTA banner */}
              <div className="ft-wa-wrap">
                <div className="ft-wa-inner">
                  <div className="ft-wa-ico">
                    <svg viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" fill="#fff" /></svg>
                  </div>
                  <div className="ft-wa-txt">
                    <div className="ft-wa-txt-h">Need Help? Chat on WhatsApp</div>
                    <div className="ft-wa-txt-s">Our support team replies fast — usually within minutes.</div>
                  </div>
                  <div className="ft-wa-num">+91 {WA_NUMBER}</div>
                  <button className="ft-wa-cta" onClick={() => openExternal(getWaUrl())}>Chat Now →</button>
                </div>
              </div>

              {/* Bottom bar */}
              <div className="ft-bot">
                <div className="ft-bot-inner">
                  <div className="ft-copy">
                    © {currentYear} <span>SUN DISTRIBUTORS Pvt. Ltd.</span> — All rights reserved.<br />
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,.30)" }}>GSTIN: 36XXXXXXXXXXXXX | Direct Selling Company</span>
                  </div>
                  <div className="ft-bot-links">
                    {["Privacy Policy", "Terms of Use", "Refund Policy", "Shipping Policy"].map((l, i, a) => (
                      <React.Fragment key={l}>
                        <button className="ft-bot-link" onClick={() => go(`/${l.toLowerCase().replace(/ /g, "-")}`)}>{l}</button>
                        {i < a.length - 1 && <span className="ft-bot-sep">|</span>}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="ft-badge"><span className="ft-badge-dot" />Verified & Secure</div>
                </div>
              </div>
            </>
          )}

          {/* ════════════════════════ LOGGED-IN FOOTER ════════════════════════ */}
          {token && (
            <>
              <div className="ft-li-main">
                {/* Brand + quick actions */}
                <div>
                  <div className="ft-li-head">
                    <img src={sun} alt="SUN DISTRIBUTORS" className="ft-li-logo" />
                    <div>
                      <div className="ft-li-name">SUN DISTRIBUTORS</div>
                      <div className="ft-li-sub">Member Portal</div>
                    </div>
                  </div>
                  <div className="ft-li-bar" />
                  <p className="ft-li-desc">
                    Access your dashboard, track earnings, manage orders and grow your network — everything in one place.
                  </p>
                  <div className="ft-li-grid">
                    {[
                      { ico: "📊", lbl: "Dashboard", to: "/dashboard" },
                      { ico: "🛍️", lbl: "Products", to: "/Products" },
                      { ico: "💰", lbl: "Wallet", to: "/wallet" },
                      { ico: "🌳", lbl: "Tree View", to: "/Tree" },
                      { ico: "🏆", lbl: "Awards", to: "/AwardsAchivement" },
                      { ico: "📦", lbl: "My Orders", to: "/Order" },
                    ].map(a => (
                      <button key={a.lbl} className="ft-li-btn" onClick={() => go(a.to)}>
                        <span>{a.ico}</span>{a.lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {/* My Account */}
                <div>
                  <div className="ft-col-head">
                    <div className="ft-col-title"><span className="ft-col-dot" />My Account</div>
                  </div>
                  <ul className="ft-links">
                    {[
                      { ico: "🧑", lbl: "My Profile", to: "/Myprofile" },
                      { ico: "🔒", lbl: "Change Password", to: "/ChangePassword" },
                      { ico: "📋", lbl: "Account Details", to: "/Referal" },
                      { ico: "📝", lbl: "My Registration", to: "/MyRegistration" },
                      { ico: "💸", lbl: "Withdraw Funds", to: "/WalletWithdraw" },
                      { ico: "📅", lbl: "Daily Wise", to: "/DailyWise" },
                      { ico: "✉️", lbl: "Welcome Letter", to: "/WelcomeLetter" },
                    ].map(l => (
                      <li key={l.lbl}>
                        <button onClick={() => go(l.to)}>
                          <span className="ft-link-icon">{l.ico}</span>{l.lbl}
                          <span className="ft-link-arr">›</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Support */}
                <div>
                  <div className="ft-col-head">
                    <div className="ft-col-title"><span className="ft-col-dot" />Support & Info</div>
                  </div>
                  <ul className="ft-links">
                    {[
                      { ico: "📞", lbl: "Contact Us", to: "/Contact" },
                      { ico: "❓", lbl: "FAQ", to: "/faq" },
                      { ico: "🔏", lbl: "Privacy Policy", to: "/privacy" },
                      { ico: "📜", lbl: "Terms & Conditions", to: "/terms" },
                      { ico: "↩️", lbl: "Refund Policy", to: "/refund" },
                      { ico: "🌐", lbl: "Main Website", ext: "https://mysun.in" },
                    ].map(l => (
                      <li key={l.lbl}>
                        <button onClick={() => l.ext ? openExternal(l.ext) : go(l.to)}>
                          <span className="ft-link-icon">{l.ico}</span>{l.lbl}
                          <span className="ft-link-arr">{l.ext ? "↗" : "›"}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* spacer for mobile fixed bottom nav */}
              <div className="ft-loggedin-spacer" />

              {/* Bottom bar */}
              <div className="ft-bot">
                <div className="ft-bot-inner">
                  <div className="ft-copy">
                    © {currentYear} <span>SUN DISTRIBUTORS Pvt. Ltd.</span> All rights reserved.
                  </div>
                  <div className="ft-bot-links">
                    {["Privacy Policy", "Terms of Use", "Refund Policy"].map((l, i, a) => (
                      <React.Fragment key={l}>
                        <button className="ft-bot-link" onClick={() => go(`/${l.toLowerCase().replace(/ /g, "-")}`)}>{l}</button>
                        {i < a.length - 1 && <span className="ft-bot-sep">|</span>}
                      </React.Fragment>
                    ))}
                  </div>
                  <button className="ft-wa-row" onClick={() => openExternal(getWaUrl())}>
                    <span className="ft-wa-row-ico"><WaIcon /></span>
                    WhatsApp Support
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </footer>
    </>
  );
}
